import { organizationTaskDetails } from "../utils/organizationTaskCategories";
import OrganizationTaskCategoryFields from "./OrganizationTaskCategoryFields";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import DataTable from "./DataTable";
import { Modal, Button } from "react-bootstrap";
const OrganizationTaskAssignments = ({
  tasks,
  canEdit,
  selectedOrgIds,
  onToggle,
  assigning,
  onSyncComplete
}) => {
  const [data, setData] = useState({
      orgs: []
    }),
    [query, setQuery] = useState(""),
    [editing, setEditing] = useState(null),
    [taskIds, setTaskIds] = useState([]);
  const [categoryRules, setCategoryRules] = useState({});
  const [busy, setBusy] = useState(false),
    [job, setJob] = useState(null),
    [error, setError] = useState("");
  const timer = useRef(null),
    base = process.env.REACT_APP_API_BASE_URL,
    token = localStorage.getItem("token");
  const load = useCallback(async () => {
    const r = await axios.get(`${base}/api/task-assignments/options`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    setData(r.data);
  }, [base, token]);
  useEffect(() => {
    load().catch(() => setError("Could not load organization assignments."));
    return () => clearTimeout(timer.current);
  }, [load]);
  const openEditor = org => {
    setError("");
    setEditing(org ? {...org, originalId:Number(org.id), isNew:false} : {id:"", name:"", isNew:true});
    setTaskIds(org?.taskIds || []);
    setCategoryRules(org?.categoryRules || {});
  };
  const save = async (remove = false) => {
    setBusy(true);
    setError("");
    try {
      if (!remove && !editing?.id) throw new Error("Select an organization.");
      const includedIds = remove ? [] : taskIds;
      if (editing.isNew && !includedIds.length) throw new Error("Select at least one task.");
      const selectedRules = Object.fromEntries(Object.entries(categoryRules).filter(([id]) => includedIds.includes(Number(id))));
      if (Object.values(selectedRules).some(value => Array.isArray(value) && !value.length)) throw new Error("Select employee categories for each limited task before saving.");
      await axios.put(`${base}/api/task-assignments/orgs/${remove ? editing.originalId : editing.id}`, {
        taskIds: includedIds,
        categoryRules: selectedRules,
        ...(editing.isNew ? {createOnly:true} : {}),
        ...(!remove && editing.originalId && editing.originalId !== Number(editing.id) ? {sourceOrgId:editing.originalId} : {})
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      await load();
      setEditing(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Could not save organization tasks.");
    } finally {
      setBusy(false);
    }
  };
  const poll = async id => {
    try {
      const {
        data: r
      } = await axios.get(`${base}/api/task-assignments/sync/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setJob(r.job);
      if (r.job.status === 'running') timer.current = setTimeout(() => poll(id), 1500);else {
        setBusy(false);
        if (r.job.status === 'failed') setError(r.job.message);
        if (r.job.status === 'complete') onSyncComplete?.();
      }
    } catch (e) {
      setBusy(false);
      setError("Could not check sync progress. The sync may still be running.");
    }
  };
  const sync = async () => {
    setBusy(true);
    setError("");
    try {
      const {
        data: r
      } = await axios.post(`${base}/api/task-assignments/sync`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setJob(r.job);
      timer.current = setTimeout(() => poll(r.job.id), 1000);
    } catch (e) {
      setBusy(false);
      setError(e.response?.data?.message || "Could not start organization sync.");
    }
  };
  const rows = useMemo(() => {
    const term = query.toLowerCase().trim();
    return (data.configuredOrgs || data.orgs.filter(org => org.taskIds?.length)).map(org => ({...org, details: organizationTaskDetails(org, tasks, data.categories || [])}))
      .filter(org => !term || `${org.name} ${org.details.map(detail => `${detail.title} ${detail.categoryLabel}`).join(" ")}`.toLowerCase().includes(term));
  }, [data.orgs, data.configuredOrgs, data.categories, tasks, query]);
  const perPage = 8;
  return <section className="mb-4">
    <div className="assign-tasks-section-header mt-3 mb-2"><div className="d-flex align-items-center align-self-center flex-wrap gap-2"><h5 className="m-0">Organization Assignment List</h5>
      {canEdit && data.canAddOrgTasks && <button type="button" className="btn btn-outline-primary" disabled={busy} onClick={() => openEditor(null)}>Add Organization Tasks</button>}
    </div><div className="nw-filter-bar assign-tasks-filters assign-tasks-program-filters">
      <input aria-label="Search organization" className="form-control" placeholder="Search organization, task or category" value={query} onChange={e => {
          setQuery(e.target.value);
        }} />
      {data.canSync && <button type="button" className="btn btn-outline-primary" disabled={busy} onClick={sync}>{busy && job?.status === 'running' ? `Syncing ${job.progress}%` : 'Sync'}</button>}
    </div></div><p className="text-muted small">Tasks are assigned automatically by organization and employee category. Admins can also check or uncheck an organization and click Save to assign its tasks manually.</p>
    {job?.status === "running" && <p className="text-muted small" role="status">{job.message || "Preparing bulk organization sync..."}{job.totalUsers ? ` (${job.syncedUsers || 0}/${job.totalUsers} users saved)` : ""}</p>}
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {job?.status === 'complete' && <div className="alert alert-success" role="status">Sync complete. Updated {job.syncedUsers} users and preserved task history.</div>}
    <DataTable
        disableEnhancement
      data={rows}
      itemsPerPage={perPage}
      tableClassName="assign-tasks-definition-table"
      emptyMessage={query ? "No organizations match your search." : "No organization tasks configured. Use Add Organization Tasks to create an assignment."}
      columns={[
        { key: "name", label: "Organization Name", width: "34%" },
        { key: "tasks", label: "Tasks / Employee Categories", getSortValue: org => org.details.map(detail => `${detail.title} ${detail.categoryLabel}`).join(" "),
          render: org => org.details.length ? <div>
            <div className="text-muted small mb-1">{org.details.length} {org.details.length === 1 ? "task" : "tasks"}</div>
            {org.details.map(detail => <div key={detail.taskId} className="mb-2">
              <div>{detail.title}</div>
              <div className="text-muted small">{detail.categoryLabel}</div>
            </div>)}
          </div> : <span className="text-muted">No tasks</span> },
        { key: "edit", label: "Edit Organization Tasks", width: 150, sortable: false, headerClassName: "text-center assignment-action-column", cellClassName: "text-center assignment-action-column",
          render: org => <div className="assignment-action-group"><button type="button" className="app-table-action-button is-edit" disabled={!canEdit || busy} onClick={() => openEditor(org)} title="Edit organization tasks" aria-label={`Edit ${org.name} tasks`}><i className="fas fa-edit" aria-hidden="true" /></button></div> },
        { key: "assigned", label: "Assigned", width: 120, headerClassName: "text-center", cellClassName: "text-center", getSortValue: org => selectedOrgIds.includes(Number(org.id)),
          render: org => <div>
            <input type="checkbox" checked={selectedOrgIds.includes(Number(org.id))} disabled={!canEdit || busy || assigning}
              title="Assign organization tasks"
              onChange={event => onToggle(Number(org.id), data.orgs, event.target.checked)} aria-label={`Assign ${org.name}`} />
          </div> },
      ]}
    />
    <Modal show={!!editing} onHide={() => !busy && setEditing(null)} size="lg"><Modal.Header closeButton><Modal.Title>{editing?.isNew ? "Add Organization Tasks" : "Edit Organization Tasks"}</Modal.Title></Modal.Header><Modal.Body>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <label className="form-label" htmlFor="organization-task-org">Organization</label>
      <select id="organization-task-org" className="form-select mb-3" value={editing?.id || ""} disabled={busy || !canEdit || !data.canAddOrgTasks}
        onChange={event => setEditing(previous => ({...previous,id:Number(event.target.value) || ""}))}>
        <option value="">Select organization...</option>
        {data.orgs.filter(org => !org.taskIds?.length || Number(org.id) === editing?.originalId).map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
      </select>
      <label className="form-label" htmlFor="organization-included-tasks">Included Tasks</label><Select inputId="organization-included-tasks" isMulti isDisabled={busy || !canEdit} options={tasks.map(t => ({
          value: Number(t.t_id),
          label: t.t_title
        }))} value={tasks.filter(t => taskIds.includes(Number(t.t_id))).map(t => ({
          value: Number(t.t_id),
          label: t.t_title
        }))} onChange={v => setTaskIds((v || []).map(o => o.value))} classNamePrefix="react-select" />
      {tasks.filter(task => taskIds.includes(Number(task.t_id))).map(task => (
        <div key={task.t_id} className="border rounded p-3 mt-3">
          <h6>{task.t_title}</h6>
          <OrganizationTaskCategoryFields label={task.t_title} categories={data.categories || []}
            categoryIds={categoryRules[task.t_id] ?? null} disabled={busy || !canEdit}
            onChange={value => setCategoryRules(previous => {
              const next = {...previous};
              if (value === null) delete next[task.t_id]; else next[task.t_id] = value;
              return next;
            })} />
        </div>
      ))}
      <p className="text-muted small mt-3">Saving applies these rules to future assignments. Current users are unchanged until you choose Sync. A task can apply to everyone while another task in the same organization applies only to selected categories. Removing all tasks removes this organization task configuration.</p>
    </Modal.Body><Modal.Footer>{editing && !editing.isNew && <Button variant="outline-danger" className="me-auto" disabled={busy || !canEdit} onClick={() => save(true)}>Remove Organization Tasks</Button>}<Button variant="secondary" disabled={busy} onClick={() => setEditing(null)}>Cancel</Button><Button variant="primary" disabled={busy || !canEdit || !editing?.id || (editing?.isNew && !taskIds.length)} onClick={() => save()}>{busy ? 'Saving...' : 'Save'}</Button></Modal.Footer></Modal>
  </section>;
};
export default OrganizationTaskAssignments;
